'use client'

import { useState, useEffect } from 'react'

export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(initialValue)
  const [isLoaded, setIsLoaded] = useState(false)

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const item = localStorage.getItem(key)
      if (item) {
        setStoredValue(JSON.parse(item) as T)
      }
    } catch (error) {
      console.error(`Failed to load ${key} from localStorage:`, error)
    }
    setIsLoaded(true)
  }, [key])

  // Set value
  const setValue = (value: T | ((val: T) => T)) => {
    setStoredValue((currentValue) => {
      const valueToStore =
        value instanceof Function ? value(currentValue) : value

      try {
        localStorage.setItem(key, JSON.stringify(valueToStore))
      } catch (error) {
        console.error(`Failed to save ${key} to localStorage:`, error)
      }

      return valueToStore
    })
  }

  // Remove value
  const removeValue = () => {
    try {
      localStorage.removeItem(key)
      setStoredValue(initialValue)
    } catch (error) {
      console.error(`Failed to remove ${key} from localStorage:`, error)
    }
  }

  return { value: storedValue, setValue, removeValue, isLoaded }
}
